import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { HttpErrorResponse } from '@angular/common/http';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateModule } from '@ngx-translate/core';
import { SiteApiKey } from 'src/app/bo/siteApiKey';
import { CreateSiteApiKey } from 'src/app/bo/createSiteApiKey';
import { UpdateSiteApiKey } from 'src/app/bo/updateSiteApiKey';
import { CreatedSiteApiKey } from 'src/app/bo/createdSiteApiKey';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HttpService } from 'src/app/services/http.service';

@Component({
  selector: 'app-mine-tracker-modal',
  templateUrl: './mine-tracker-modal.component.html',
  styleUrls: ['./mine-tracker-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatDatepickerModule, TranslateModule],
})
export class MineTrackerModalComponent implements OnInit {

  // Set via BsModal initialState
  siteId: number;
  selectedApiKey: SiteApiKey;
  isModif: boolean;
  onApiKeyCreated: (createdApiKey: CreatedSiteApiKey) => void;

  protected readonly showError = signal(false);
  protected readonly minDate = new Date();

  // Getter/setter backed by a signal so [(ngModel)] works while state is reactive
  private readonly _expirationDate = signal<Date | null>(null);
  get expirationDate(): Date | null { return this._expirationDate(); }
  set expirationDate(val: Date | null) { this._expirationDate.set(val); }

  private readonly http = inject(HttpService);
  private readonly bsModalRef = inject(BsModalRef);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly bsModalService = inject(BsModalService);

  ngOnInit(): void {
    if (this.selectedApiKey) {
      this.selectedApiKey.siteId = this.siteId;
      if (this.selectedApiKey.expiresAt) {
        this._expirationDate.set(new Date(this.selectedApiKey.expiresAt));
      }
    }
  }

  protected onSubmit(): false | void {
    if (!this.selectedApiKey.name) {
      this.showError.set(true);
      setTimeout(() => this.showError.set(false), 1500);
      return false;
    }

    const url = 'SiteApiKeys';
    let request;

    if (this.isModif) {
      const updateDto: UpdateSiteApiKey = { name: this.selectedApiKey.name };
      request = this.http.put<UpdateSiteApiKey, any>(url + '/' + this.selectedApiKey.siteApiKeyId, updateDto);
    } else {
      const createDto: CreateSiteApiKey = {
        name: this.selectedApiKey.name,
        siteId: this.siteId,
        expiresAt: this._expirationDate(),
      };
      request = this.http.post<CreateSiteApiKey, CreatedSiteApiKey>(url, createDto);
    }

    this.fonctionsService.loading_screen(true);

    request.subscribe({
      next: result => {
        this.fonctionsService.launch_toast(true);
        this.fonctionsService.loading_screen(false);
        if (!this.isModif && this.onApiKeyCreated) {
          this.onApiKeyCreated(result as CreatedSiteApiKey);
        }
        this.bsModalService.setDismissReason('saved');
        this.bsModalRef.hide();
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          console.log(error.error);
        }
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
        this.bsModalService.setDismissReason('error');
        this.bsModalRef.hide();
      },
    });
  }

  protected delete(): void {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    const url = 'SiteApiKeys/' + this.selectedApiKey.siteApiKeyId;
    this.fonctionsService.loading_screen(true);

    this.http.delete<any>(url).subscribe({
      next: () => {
        this.fonctionsService.launch_toast(true);
        this.fonctionsService.loading_screen(false);
        this.bsModalService.setDismissReason('saved');
        this.bsModalRef.hide();
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          console.log(error.error);
        }
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
        this.bsModalService.setDismissReason('error');
        this.bsModalRef.hide();
      },
    });
  }

  protected nettoyerDates(): void {
    this._expirationDate.set(null);
  }

  protected close(): void {
    this.bsModalRef.hide();
  }
}
